import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from '@/context/Modal.context';
import {
  MODAL_TYPES,
  useQualityControlTool,
} from '@/context/Qualitycontrol.context';
import {
  getWarehouseConfig,
  parseDecimal,
  LENGTH_FORMAT,
} from '@/helpers/unit';
import { useGetDimensionTypes } from '@/hooks/defect-details/use-get-dimension-types';
import { useProduct } from '@/hooks/use-product';
import {
  ActualMeasurement,
  FormDimension,
} from '@/types/interfaces/defect-details';
import MeasurementAssistantForm, {
  FormValues,
} from './MeasurementAssistantForm';
import { DisplayRow, SellerMeasurement } from './types';
import { ValidationResult } from './validation';
import QcModal from '../Modal/Modal';

const MeasurementAssistantModal = () => {
  const { t } = useTranslation();
  const {
    modal: { modalInfo },
    setModalInfo,
  } = useModal();
  const {
    qcData,
    actualMeasurements,
    setActualMeasurements,
    measurementAssistantBagType,
    setMeasurementAssistantBagType,
  } = useQualityControlTool();
  const { productId = '' } = useProduct();
  const { data: dimensionTypes = [], isLoading: isDimensionTypesLoading } =
    useGetDimensionTypes(productId);

  const resetRef = useRef<(() => void) | null>(null);
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null);
  const [formValues, setFormValues] = useState<FormValues | null>(null);

  const { checkpointId, checkpointName } = (modalInfo || {}) as {
    checkpointId: number;
    checkpointName: string;
  };

  const DIMENSION_IDS = [19, 20, 30, 31, 32];
  const INCORRECT_MEASUREMENTS_DEFECT_ID = 243;

  // Build a map of seller-provided values keyed by dimension id
  const rawMeasurements = qcData?.product?.dimensions?.measurements ?? {};
  const sellerValueById = new Map<number, SellerMeasurement>(
    Object.entries(rawMeasurements)
      .filter(
        ([, entry]) =>
          entry.value !== undefined &&
          entry.id !== undefined &&
          DIMENSION_IDS.includes(entry.id as number)
      )
      .map(([key, entry]) => {
        const primaryValue = parseDecimal(entry.value ?? '0');
        const convertedValue = entry.convertedValue ?? 0;
        const isInches = entry.unit === 'in';
        const valueCm = isInches ? convertedValue : primaryValue;
        const valueIn = isInches ? primaryValue : convertedValue;

        const measurement: SellerMeasurement = {
          key,
          name: entry.en ?? entry.fr ?? key,
          valueCm,
          valueIn,
          id: entry.id as number,
        };
        return [measurement.id, measurement];
      })
  );
  // Display rows: canonical list from API, seller value matched by legacyId
  const displayRows: DisplayRow[] = dimensionTypes
    .filter(
      (d) => d.legacyId !== undefined && DIMENSION_IDS.includes(d.legacyId)
    )
    .map((d) => ({
      dimension: d,
      sellerMeasurement: sellerValueById.get(d.legacyId!),
    }));

  // Prefill: session-confirmed values win, then backend record values
  const prefillSource: ActualMeasurement[] | null =
    actualMeasurements.length > 0
      ? actualMeasurements
      : qcData?.record?.actualMeasurements ?? null;

  const prefillById = new Map<number, ActualMeasurement>(
    (prefillSource ?? [])
      .filter((m): m is ActualMeasurement => m?.id !== undefined)
      .map((m) => [m.id, m])
  );

  const initialFormValues: FormValues = {
    bagType: measurementAssistantBagType,
    agentValues: displayRows.map((row) => {
      const legacyId = row.dimension.legacyId;
      if (legacyId === undefined) return undefined;
      return prefillById.get(legacyId)?.value;
    }),
  };

  const handleResultChange = (
    result: ValidationResult | null,
    values: FormValues
  ) => {
    setValidationResult(result);
    setFormValues(values);
  };

  const handleClose = () => {
    setModalInfo({ modalType: '', modalInfo: {} });
  };

  const handleReset = () => {
    if (resetRef.current) resetRef.current();
    setValidationResult(null);
    setFormValues(null);
  };

  const allFilled =
    validationResult !== null &&
    formValues !== null &&
    formValues.bagType !== null &&
    formValues.agentValues.every(
      (v) => v !== undefined && v !== '' && parseDecimal(v) > 0
    );

  const handleConfirm = () => {
    if (!allFilled || !validationResult) return;

    // Persist agent-entered measurements regardless of OK/KO outcome
    const config = getWarehouseConfig();
    const unit = config?.lengthFormat === LENGTH_FORMAT.IN ? 'in' : 'cm';
    const actualMeasurementsList: ActualMeasurement[] = (
      formValues?.agentValues ?? []
    )
      .map((value, idx) => {
        const row = displayRows[idx];
        if (!row || !value) return null;
        return {
          id: row.dimension.legacyId,
          value: String(parseDecimal(value)),
          unit,
        };
      })
      .filter((m): m is ActualMeasurement => m !== null);
    setActualMeasurements(actualMeasurementsList);
    setMeasurementAssistantBagType(formValues.bagType);

    if (validationResult.incorrectMeasurement) {
      // Build prefilledDimensions from agent values matched to dimension ids
      const prefilledDimensions: FormDimension = (
        formValues?.agentValues ?? []
      ).map((value, idx) => {
        const row = displayRows[idx];
        if (!row || !value) return undefined;
        return { id: row.dimension.id, value };
      });

      setModalInfo({
        modalType: MODAL_TYPES.DEFECT_CREATION,
        modalInfo: {
          checkpointId,
          checkpointName,
          preselectedDefectTypeId: INCORRECT_MEASUREMENTS_DEFECT_ID,
          prefilledDimensions,
          isFromMeasurementAssistant: true,
        },
      });
    } else {
      handleClose();
    }
  };

  return (
    <QcModal
      open={true}
      id="measurement-assistant-modal"
      header={t('QC_V2.MEASUREMENT.TITLE')}
      maxWidth="md"
      disableBackdropClick
      dialogProps={{
        disableEscapeKeyDown: true,
      }}
      content={
        <Box sx={{ minWidth: 560 }}>
          {isDimensionTypesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={18} />
            </Box>
          ) : displayRows.length > 0 ? (
            <MeasurementAssistantForm
              displayRows={displayRows}
              onResultChange={handleResultChange}
              resetRef={resetRef}
              initialFormValues={initialFormValues}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 2,
                color: 'text.secondary',
              }}
            >
              {t('QC_V2.MEASUREMENT.NO_MEASUREMENTS_AVAILABLE')}
            </Box>
          )}
        </Box>
      }
      actionButtons={
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            disabled={!allFilled}
            onClick={handleConfirm}
          >
            {t('QC_V2.MEASUREMENT.CONFIRM')}
          </Button>
          <Button onClick={handleReset}>{t('QC_V2.MEASUREMENT.RESET')}</Button>
          <Button onClick={handleClose}>{t('QC_V2.MEASUREMENT.CANCEL')}</Button>
        </Box>
      }
    />
  );
};

export default MeasurementAssistantModal;
